import { useEffect } from 'react';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';
import copyIcon from '../../assets/icons/copy.svg';

const BAR_MARKER = 'honeCodeBar';

/*
 * Gives every code block the mock's header bar: the language on the left, a
 * copy affordance on the right, both mono on the ink surface.
 *
 * mkdocs-material only emits its own copy button when a repo opts into the
 * `content.code.copy` feature, so the bar is added here instead and the stock
 * button is hidden in `readerStyles`. The work is plain DOM rather than a
 * portal because one addon has to reach every `.highlight` in the page.
 */
const languageOf = (block: Element): string => {
  const holder = block.classList.contains('highlight')
    ? block
    : (block.closest('[class*="language-"]') ?? block);
  const match = Array.from(holder.classList).find(name =>
    name.startsWith('language-'),
  );
  return match ? match.slice('language-'.length) : 'code';
};

const buildBar = (doc: Document, block: HTMLElement): HTMLElement => {
  const bar = doc.createElement('div');
  bar.className = 'hone-code-bar';

  const language = doc.createElement('span');
  language.textContent = languageOf(block);
  bar.appendChild(language);

  const spacer = doc.createElement('span');
  spacer.className = 'hone-code-bar__spacer';
  bar.appendChild(spacer);

  const copy = doc.createElement('button');
  copy.type = 'button';
  copy.className = 'hone-code-copy';

  const glyph = doc.createElement('img');
  glyph.src = copyIcon;
  glyph.alt = '';
  glyph.className = 'hone-code-copy__icon';
  copy.appendChild(glyph);

  const label = doc.createElement('span');
  label.textContent = 'copy';
  copy.appendChild(label);
  copy.addEventListener('click', () => {
    const code = block.querySelector('code')?.textContent ?? '';
    navigator.clipboard?.writeText(code).then(
      () => {
        label.textContent = 'copied';
        window.setTimeout(() => {
          label.textContent = 'copy';
        }, 1500);
      },
      () => {
        label.textContent = 'copy failed';
      },
    );
  });
  bar.appendChild(copy);

  return bar;
};

export const CodeBlockAddon = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;
    const doc = shadowRoot.ownerDocument;

    const decorate = () => {
      // Only pygments-wrapped blocks: a bar cannot be prepended into a bare
      // <pre> without landing inside the preformatted text. Line-numbered
      // blocks nest a second `.highlight` inside the table; only the outer one
      // is a surface.
      const blocks =
        shadowRoot.querySelectorAll<HTMLElement>('.md-typeset .highlight');
      blocks.forEach(block => {
        if (block.dataset[BAR_MARKER]) return;
        if (block.parentElement?.closest('.highlight')) return;
        block.dataset[BAR_MARKER] = 'true';
        block.prepend(buildBar(doc, block));
      });
    };

    decorate();
    // The reader swaps content in after the shadow root exists on some
    // transitions, so keep watching until the addon unmounts.
    const observer = new MutationObserver(decorate);
    observer.observe(shadowRoot, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      // The marker has to come off with the bar: React re-runs effects in
      // development, and a block left marked would never get a second bar.
      shadowRoot
        .querySelectorAll<HTMLElement>('[data-hone-code-bar]')
        .forEach(block => delete block.dataset[BAR_MARKER]);
      shadowRoot
        .querySelectorAll<HTMLElement>('.hone-code-bar')
        .forEach(bar => bar.remove());
    };
  }, [shadowRoot]);

  return null;
};
